// Small shared pieces: pills, phone links, KPI tiles, progress bars, the trend chart, tabs, error boundary.
import { Component, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { fmtPhone } from "../lib/format";
import { Native } from "../lib/native";
import { Icon } from "./Icon";

export const Pill = ({ children, tone = "" }: { children: ReactNode; tone?: string }) => <span className={`pill ${tone}`}>{children}</span>;

const TIER_NAME: Record<string, string> = { diamond: "Diamond", gold: "Gold", silver: "Silver", long_tail: "Long tail" };
export const TierPill = ({ tier, label }: { tier?: string | null; label?: string | null }) =>
  tier && tier !== "none" ? <Pill tone={tier === "long_tail" ? "" : tier}>{label || TIER_NAME[tier] || tier}</Pill> : null;

const STAGE_TONE: Record<string, string> = { active: "good", new: "good", lapsing: "warn", dormant: "bad", churned: "bad", pre_fop: "brand" };
export const STAGE_NAME: Record<string, string> = { pre_fop: "Pre-FOP", new: "New (FOP)", active: "Active", lapsing: "Lapsing", dormant: "Dormant", churned: "Churned" };
export const StagePill = ({ stage, label }: { stage?: string | null; label?: string | null }) =>
  <Pill tone={STAGE_TONE[stage ?? ""] || ""}>{label || STAGE_NAME[stage ?? ""] || stage}</Pill>;

const STATUS_TONE: Record<string, string> = {
  approved: "good", sent: "good", pending_approval: "warn", rejected: "bad", queued: "warn", ready: "warn", done: "good", verified: "good",
  stale: "warn", unknown: "bad", failed: "bad", blocked: "bad", promised: "brand", raised: "warn", credited: "good", ok: "good",
  below_quote: "bad", overcharge: "bad", price_queued: "warn", price_applied: "good", open: "warn", ack: "brand", resolved: "good",
  pending_approval_refund: "warn",
};
/** A workflow status as a coloured pill ("pending approval"). */
export const Status = ({ s }: { s?: string | null }) => <Pill tone={STATUS_TONE[s ?? ""] || ""}>{(s || "").replace(/_/g, " ")}</Pill>;

export const Prio = ({ p }: { p: number }) => <span className={p <= 1 ? "p1" : p === 2 ? "p2" : "muted"}>P{p}</span>;

/** Dial: through the Android app when inside it, tel: otherwise. `children` replaces the default "📞 +91 …" label. */
export function TelLink({ phone, children, className }: { phone?: string | null; children?: ReactNode; className?: string }) {
  if (!phone) return null;
  const label = children ?? <><Icon name="phone" /> {fmtPhone(phone)}</>;
  if (Native) return <a href="#" className={className} onClick={(e) => { e.preventDefault(); Native!.dial(phone); }}>{label}</a>;
  return <a href={`tel:+91${phone}`} className={className}>{label}</a>;
}

export const Bar = ({ pct, cls = "" }: { pct?: number | null; cls?: string }) =>
  <div className={`bar ${cls}`}><i style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%` }} /></div>;

export const Kpi = ({ l, v, s, vClass = "", title, children }: { l: ReactNode; v: ReactNode; s?: ReactNode; vClass?: string; title?: string; children?: ReactNode }) => (
  <div className="kpi" title={title}><div className="l">{l}</div><div className={`v ${vClass}`}>{v}</div>{s != null && <div className="s">{s}</div>}{children}</div>
);

export const Banner = ({ b }: { b?: { level: string; text: string } | null }) => b ? <div className={`banner ${b.level}`}>{b.text}</div> : null;

/** Monthly bars with value labels; the current month is highlighted. */
export function TrendChart<T extends Record<string, unknown> & { month: string }>({ rows, k, fmt }: { rows: T[]; k: keyof T & string; fmt: (v: number) => string }) {
  const W = 560, H = 160, pad = 26, bw = (W - pad * 2) / Math.max(1, rows.length);
  const val = (r: T) => Number(r[k]) || 0;
  const max = Math.max(1, ...rows.map(val));
  return (
    <svg className="chart" viewBox={`0 0 ${W} ${H + 20}`} role="img" aria-label={`${k} trend`}>
      {rows.map((r, i) => {
        const h = Math.round((H - 30) * val(r) / max), x = pad + i * bw + 6, y = H - h, mid = x + (bw - 12) / 2;
        return (
          <g key={r.month}>
            <rect className={`b ${i === rows.length - 1 ? "cur" : ""}`} x={x} y={y} width={bw - 12} height={h} rx="3"><title>{`${r.month}: ${fmt(val(r))}`}</title></rect>
            <text x={mid} y={y - 5} textAnchor="middle">{fmt(val(r))}</text>
            <text x={mid} y={H + 14} textAnchor="middle">{`${r.month.slice(5)}/${r.month.slice(2, 4)}`}</text>
          </g>
        );
      })}
    </svg>
  );
}

/** Tabs kept in the URL (?tab=…), so Back and shared links land on the same tab. Clicking one drops the other
 * query params unless `keep`. `className`: "tabs", "seg" or "seg wide". */
export function UrlTabs({ tabs, cur, param = "tab", className = "tabs", keep = false }: {
  tabs: (readonly [string, ReactNode] | false | null | undefined)[]; cur: string; param?: string; className?: string; keep?: boolean;
}) {
  const [, setSp] = useSearchParams();
  return (
    <div className={className} role="tablist">
      {tabs.filter((t): t is readonly [string, ReactNode] => !!t).map(([k, l]) => (
        <button key={k} role="tab" aria-selected={k === cur} className={k === cur ? "on" : ""}
          onClick={() => setSp((p) => { const n = keep ? new URLSearchParams(p) : new URLSearchParams(); n.set(param, k); return n; })}>
          {l}
        </button>
      ))}
    </div>
  );
}

/** A screen that failed to load shows why, in place; the rest of the app keeps working. `quiet` for optional parts. */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: unknown; quiet?: boolean }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidUpdate(prev: { resetKey?: unknown }) { if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null }); }
  render() {
    if (!this.state.error) return this.props.children;
    return this.props.quiet ? null : <div className="banner red">{this.state.error.message}</div>; // quiet: an optional part just drops out
  }
}
